import type { FragmentWriterInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import {
	type waitForEditionChangeQuery$data,
	type waitForEditionChangeQuery$variables,
} from './__generated__/waitForEditionChangeQuery.graphql.ts';
import { waitForEditionChangeQuery } from './waitForEditionChangeQuery.graphql.ts';
import {
	CHANGE_EDITION_POLL_DURATION_MINS,
	CHANGE_EDITION_POLL_INTERVAL_SECS,
} from './changeEditionPollConstants.ts';

type ConfluenceEdition = NonNullable<
	NonNullable<waitForEditionChangeQuery$data['tenant']>['editions']
>['confluence'];
const timeNow = () => new Date().getTime();

export const TimeoutError: Error = new Error('TIMEOUT');

/**
 * Get a promise that polls for changes to the edition and resolves with the new edition when it has changed from the current edition.
 *
 * @param {ConfluenceEdition|undefined} currentEdition - will keep polling until the edition no longer matches this or timeout.
 *                                                       If falsy, the promise will reject immediately.
 * @param {FragmentWriterInterop} writeFragmentInterop - fragment writer function for cache updates when experiment is enabled
 */
export const waitForEditionChange = (
	currentEdition: ConfluenceEdition | null | undefined,
	writeFragmentInterop: FragmentWriterInterop<any>,
): Promise<ConfluenceEdition> => {
	if (!currentEdition) {
		return Promise.reject(new Error('No currentEdition supplied'));
	}

	const pollEndTime = new Date(timeNow() + CHANGE_EDITION_POLL_DURATION_MINS * 60 * 1000).getTime();

	const pollAndResolve = (
		resolve: (value: ConfluenceEdition) => void,
		reject: (reason: Error) => void,
	) => {
		if (timeNow() > pollEndTime) {
			reject(TimeoutError);
		}
		queryInterop<waitForEditionChangeQuery$data, waitForEditionChangeQuery$variables>({
			query: waitForEditionChangeQuery,
			errorPolicy: 'all',
			fetchPolicy: 'network-only',
		})
			.then((response) => {
				const polledEdition = response.data?.tenant?.editions?.confluence;
				if (polledEdition && currentEdition !== polledEdition) {
					// Write the latest edition to cache.
					writeFragmentInterop({
						fragmentId: '$ROOT_QUERY.confluence_tenantContext',
						data: {
							editions: {
								confluence: polledEdition,
								__typename: 'ConfluenceEditions',
							},
							__typename: 'Tenant',
						},
					});
					resolve(polledEdition);
				} else {
					setTimeout(() => {
						pollAndResolve(resolve, reject);
					}, CHANGE_EDITION_POLL_INTERVAL_SECS * 1000);
				}
			})
			.catch(reject);
	};

	return new Promise<ConfluenceEdition>((resolve, reject) => pollAndResolve(resolve, reject));
};
